/* Bradoria Cart v2 — drawer + qty stepper + remove + promo + upsell add.
 *
 * Implements the behavioral contract from MiniCart.jsx + Cart.jsx:
 *   - [data-brd-cart-trigger] (preferred) OR .brd-topnav__icon-btn--bag
 *     toggles the drawer.
 *   - ESC + scrim + close button dismiss it. Body scroll is locked.
 *   - Qty steppers + remove + close-x hit /cart/change.js (line-key based).
 *   - Promo form posts to /cart/update.js with the `discount` param.
 *   - Upsell + cards POST /cart/add.js then open the drawer.
 *   - After every mutation, the current page HTML is fetched and BOTH
 *     the drawer and the cart-page surface are re-rendered in place.
 *
 * prefers-reduced-motion → skip the slide animation entirely.
 * No deps.
 */
(function () {
  'use strict';

  var ROUTES = (window.Shopify && window.Shopify.routes) || {};
  var ROOT   = ROUTES.root || '/';
  var CHANGE = ROOT + 'cart/change.js';
  var UPDATE = ROOT + 'cart/update.js';
  var ADD    = ROOT + 'cart/add.js';

  var drawer = document.querySelector('[data-brd-cart-drawer]');
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Drawer open / close ---------- */
  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-cart-open');
    if (prefersReduced) drawer.classList.add('is-instant');
    document.addEventListener('keydown', onEsc);
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('is-cart-open');
    document.removeEventListener('keydown', onEsc);
  }
  function onEsc(e) { if (e.key === 'Escape') closeDrawer(); }

  /* Bind triggers (explicit data attr preferred, header bag fallback) */
  function bindTriggers() {
    var explicit = document.querySelectorAll('[data-brd-cart-trigger]');
    var fallback = document.querySelectorAll('.brd-topnav__icon-btn--bag');
    var nodes = explicit.length ? explicit : fallback;
    nodes.forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (!drawer) return; /* keep href fallback for no-JS */
        e.preventDefault();
        openDrawer();
      });
    });
    if (drawer) {
      drawer.querySelectorAll('[data-brd-cart-close]').forEach(function (el) {
        el.addEventListener('click', closeDrawer);
      });
    }
  }

  /* ---------- Re-render drawer + cart page from current URL ---------- */
  function refreshSurfaces(opts) {
    opts = opts || {};
    return fetch(window.location.pathname + window.location.search, {
      headers: { 'X-Requested-With': 'fetch' },
      cache: 'no-store'
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        /* Refresh drawer */
        var fresh = doc.querySelector('#brd-cart-drawer');
        if (fresh && drawer) {
          var wasOpen = drawer.classList.contains('is-open');
          drawer.innerHTML = fresh.innerHTML;
          if (wasOpen || opts.openAfter) {
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.documentElement.classList.add('is-cart-open');
            document.addEventListener('keydown', onEsc);
          }
          bindLineHandlers(drawer);
          bindUpsellHandlers(drawer);
          drawer.querySelectorAll('[data-brd-cart-close]').forEach(function (el) {
            el.addEventListener('click', closeDrawer);
          });
        }

        /* Refresh cart page (if user is on /cart) */
        var page = document.querySelector('[data-brd-cart-page]');
        var freshPage = doc.querySelector('[data-brd-cart-page]');
        if (page && freshPage) {
          page.innerHTML = freshPage.innerHTML;
          bindLineHandlers(page);
          bindSummaryHandlers(page);
        }

        /* Refresh header cart count badge */
        var badge = document.querySelector('.brd-topnav__cart-count');
        var freshBadge = doc.querySelector('.brd-topnav__cart-count');
        if (badge && !freshBadge) badge.remove();
        else if (badge && freshBadge) badge.textContent = freshBadge.textContent;
        else if (!badge && freshBadge) {
          var bag = document.querySelector('.brd-topnav__icon-btn--bag');
          if (bag) bag.appendChild(freshBadge.cloneNode(true));
        }
      });
  }

  /* ---------- Cart mutations ---------- */
  function changeLine(key, qty) {
    return fetch(CHANGE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  function applyDiscount(code) {
    return fetch(UPDATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then(function (r) { return r.json(); });
  }

  function addVariant(variantId, qty) {
    var body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(qty || 1));
    return fetch(ADD, { method: 'POST', body: body, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ---------- Line handlers (qty + remove) ---------- */
  function bindLineHandlers(root) {
    if (!root) return;
    root.querySelectorAll('[data-brd-cart-line]').forEach(function (line) {
      var key = line.getAttribute('data-line-key');
      var input = line.querySelector('[data-brd-cart-qty-input]');
      var dec = line.querySelector('[data-brd-cart-qty-dec]');
      var inc = line.querySelector('[data-brd-cart-qty-inc]');
      var rm  = line.querySelectorAll('[data-brd-cart-remove]');

      function setQty(q) {
        q = Math.max(0, Math.min(99, q | 0));
        if (input) input.value = q;
        line.classList.add('is-updating');
        changeLine(key, q).then(function () {
          return refreshSurfaces();
        }).catch(function () {
          line.classList.remove('is-updating');
        });
      }
      if (dec) dec.addEventListener('click', function () { setQty(parseInt(input.value, 10) - 1); });
      if (inc) inc.addEventListener('click', function () { setQty(parseInt(input.value, 10) + 1); });
      if (input) {
        var debounce;
        input.addEventListener('change', function () {
          clearTimeout(debounce);
          debounce = setTimeout(function () { setQty(parseInt(input.value, 10)); }, 200);
        });
      }
      rm.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          setQty(0);
        });
      });
    });
  }

  /* ---------- Cart-page summary handlers (promo) ---------- */
  function bindSummaryHandlers(root) {
    if (!root) return;
    var promo = root.querySelector('[data-brd-promo-form]');
    if (promo) {
      promo.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = root.querySelector('[data-brd-promo-input]');
        var msg = root.querySelector('[data-brd-promo-msg]');
        var code = (input && input.value || '').trim();
        if (!code) return;
        if (msg) { msg.textContent = ''; msg.className = 'brd-cart-summary__promo-msg'; }
        applyDiscount(code).then(function () {
          return refreshSurfaces().then(function () {
            var freshMsg = document.querySelector('[data-brd-promo-msg]');
            if (freshMsg) {
              freshMsg.textContent = '✓ ' + code + ' — ' + (freshMsg.getAttribute('data-applied-text') || 'applied');
              freshMsg.className = 'brd-cart-summary__promo-msg is-success';
            }
          });
        }).catch(function () {
          if (msg) {
            msg.textContent = msg.getAttribute('data-invalid-text') || 'That code is not valid';
            msg.className = 'brd-cart-summary__promo-msg is-error';
          }
        });
      });
    }
  }

  /* ---------- Upsell handlers (+ button on drawer upsell cards) ---------- */
  function bindUpsellHandlers(root) {
    if (!root) return;
    root.querySelectorAll('[data-brd-upsell-add]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var idInput = form.querySelector('[name="id"]');
        var qtyInput = form.querySelector('[name="quantity"]');
        if (!idInput) return;
        var variantId = idInput.value;
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        addVariant(variantId, qty).then(function () {
          return refreshSurfaces({ openAfter: true });
        });
      });
    });
  }

  /* ---------- Init ---------- */
  function init() {
    bindTriggers();
    bindLineHandlers(drawer);
    bindUpsellHandlers(drawer);
    var page = document.querySelector('[data-brd-cart-page]');
    bindLineHandlers(page);
    bindSummaryHandlers(page);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
